import type { Epic } from '../../index.js'
import { EMPTY, filter, merge, mergeMap, of } from 'rxjs'
import { withLatestFrom } from 'rxjs/operators'
import { connectionReady } from '../../Slice/Application/ApplicationSlice.js'
import { orderSlice } from '../../Slice/Order/OrderSlice.js'
import { projectSlice } from '../../Slice/Project/ProjectSlice.js'

/*
 * Re-establish realtime subscriptions after a websocket reconnect.
 *
 * A reconnect opens a brand-new backend session whose subscription set starts
 * empty (see Websocket.kt @OnOpen), so the server filters out every event until
 * the client re-subscribes. Nothing did that before, so we could sit on a
 * perfectly healthy socket silently receiving nothing for the resource on screen.
 *
 * Re-firing the active order/project fetch fixes both halves of the problem:
 * its *RequestSuccess re-sends subscription-change via updateSubscriptionEpic
 * (re-subscribe), and the refetch backfills anything that changed during the
 * disconnect gap.
 *
 * connectionReady fires on every GENUINE socket open (raised from the
 * openObserver in ConnectToServerEpic — never on a mere connection attempt or a
 * failed reconnect), so a dead/unreachable backend produces zero resyncs here.
 *
 * isReconnect (not skip(1)) decides what counts as a reconnect: the very first
 * successful open needs no resync because the page that mounted loads its own
 * data, but "first open" is not the same as "first emission". If the initial
 * attempt failed and a later one succeeded, skip(1) swallowed that success and
 * left us on a healthy socket whose server-side subscription set was never
 * populated — connected, and silently receiving nothing.
 */
export const resyncOnReconnectEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(connectionReady.match),
    filter((action) => action.payload.isReconnect),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const order = state.order.order
      const orderId = order && order !== 'LOADING' ? order.id : undefined
      const projectId = state.project.currentProject?.id

      /* Note: This currently won't fix any lost sample events - but that is a trade-off we'll need to live with */
      return merge(
        orderId ? of(orderSlice.actions.getOrderRequest({ orderId })) : EMPTY,
        projectId ? of(projectSlice.actions.getProjectRequest({ projectId })) : EMPTY
      )
    })
  )
